import React, { useEffect, useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { ApplicationService } from "../../services/ApplicationService";
import { Application } from "../../model_gen/application";
import { PermissionsService } from "../../services/PermissionsService";
import { Key } from "../../model_gen/key";
import { useAuth0 } from "@auth0/auth0-react";
import { usePreventExit } from "../../utils/usePrevent";
import useStore from "../../store/useStore";
import { BillingService } from "../../services/BillingService";
import { PaymentMethod } from "../../model_gen/billing";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "../ui/collapsible";
import CodeBlock from "../shared/codeblock/CodeBlock";
import {
  CheckIcon,
  TerminalIcon,
  CodeIcon,
  CreditCardIcon,
  ExternalLinkIcon,
  ArrowRightIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";

interface StepProps {
  stepNumber: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const Step: React.FC<StepProps> = ({
  stepNumber,
  title,
  description,
  icon,
  completed,
  children,
  defaultOpen = false,
}) => {
  return (
    <Collapsible defaultOpen={defaultOpen}>
      <Card
        className={cn(
          "transition-all duration-200 border-border",
          completed && "bg-card"
        )}>
        <CollapsibleTrigger className="w-full p-6 hover:bg-accent/20 transition-colors">
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0 mt-1">
              <div
                className={cn(
                  "flex items-center justify-center w-6 h-6 rounded-full text-xs font-gc-med transition-colors",
                  completed
                    ? "bg-maitai-green text-white"
                    : "bg-muted text-muted-foreground"
                )}>
                {completed ? (
                  <CheckIcon className="w-3 h-3" />
                ) : (
                  <span>{stepNumber}</span>
                )}
              </div>
            </div>

            <div className="flex-1 text-left">
              <h3 className="text-lg font-gc-med text-foreground mb-1">
                {title}
              </h3>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-6 pb-6">
            <div className="pl-12">{children}</div>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

const Tutorial: React.FC = () => {
  const { apiKey } = usePermissions();
  const { dataReadyTutorial, setDataReadyTutorial } = useStore();

  const [apiToken, setApiToken] = useState<string>("");
  const [companyId, setCompanyId] = useState<number>(0);
  const { user } = useAuth0();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    setInitialLoading(true);

    let keysLoaded = false;
    let paymentMethodsLoaded = false;

    const checkAllLoaded = () => {
      if (keysLoaded && paymentMethodsLoaded) {
        setInitialLoading(false);
      }
    };

    const applicationsSubscription = ApplicationService.getApplications(
      null,
      apiKey
    ).subscribe(
      (apps: Application[]) => {
        setDataReadyTutorial(Boolean(apps && apps.length > 0));
      },
      (error: any) => {
        console.error("Error fetching applications:", error);
        setDataReadyTutorial(true);
      }
    );

    PermissionsService.getKeysFiltered("maitai", null, apiKey).subscribe(
      (keys: Key[]) => {
        setCompanyId(keys[0].company_id);
        setApiToken(keys[0].key_value);
        keysLoaded = true;
        checkAllLoaded();
      },
      (error: any) => {
        console.error(error);
        keysLoaded = true;
        checkAllLoaded();
      }
    );

    BillingService.getPaymentMethods(
      setLoadingPaymentMethods,
      apiKey
    ).subscribe(
      (methods: PaymentMethod[]) => {
        setPaymentMethods(methods);
        setLoadingPaymentMethods(false);
        paymentMethodsLoaded = true;
        checkAllLoaded();
      },
      (error: any) => {
        console.error("Error fetching payment methods:", error);
        setLoadingPaymentMethods(false);
        paymentMethodsLoaded = true;
        checkAllLoaded();
      }
    );

    return () => {
      applicationsSubscription?.unsubscribe?.();
    };
  }, [apiKey]);

  usePreventExit(!dataReadyTutorial, ["/billing", "/admin"]);

  const installTabs = [
    {
      key: "1",
      label: "Python",
      language: "bash",
      children: "pip install maitai-python",
    },
    {
      key: "2",
      label: "JavaScript",
      language: "bash",
      children: "npm install maitai",
    },
  ];

  const codeExampleTabs = [
    {
      key: "1",
      label: "Python",
      language: "python",
      children: `import maitai

client = maitai.Maitai(
    maitai_api_key="${apiToken}"
)

messages = [
    {"role": "system", "content": "You are a helpful assistant"},
    {"role": "user", "content": "Generate numbers 1-10"},
]

response = client.chat.completions.create(
    messages=messages,
    model="llama3-70b-8192",
    application="YOUR_APP_PROD",
    intent="CONVERSATION",
    session_id="CONVERSATION_ID",
)`,
    },
    {
      key: "2",
      label: "JavaScript",
      language: "javascript",
      children: `import Maitai from "maitai";

const maitai = new Maitai({
  maitaiApiKey: "${apiToken}"
});

const messages = [
    {"role": "system", "content": "You are a helpful assistant"},
    {"role": "user", "content": "Generate numbers 1-10"},
];

const response = await maitai.chat.completions.create({
    messages: messages,
    model: "llama3-70b-8192",
    application: "YOUR_APP_PROD",
    intent: "CONVERSATION",
    session_id: "CONVERSATION_ID",
});`,
    },
  ];

  const allStepsComplete = dataReadyTutorial && paymentMethods.length > 0;
  const completedSteps = [
    dataReadyTutorial,
    dataReadyTutorial,
    paymentMethods.length > 0,
  ].filter(Boolean).length;
  const userName =
    user?.given_name ||
    user?.name?.split(" ")[0] ||
    user?.nickname ||
    user?.email;

  if (initialLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-12">
        {/* Header Section */}
        <div className="text-center space-y-6 pb-8 border-b border-border">
          <div className="space-y-3">
            <h1 className="text-4xl font-gc-demi-bold text-foreground leading-tight">
              Welcome to Maitai
              {userName && (
                <span className="text-muted-foreground">, {userName}</span>
              )}
            </h1>
            <p className="text-muted-foreground text-xl font-gc-reg">
              Get reliable LLM output in 3 steps
            </p>
          </div>

          {/* Loading indicator */}
          <div className="flex items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-maitai-green"></div>
            <span className="text-sm text-muted-foreground">Loading...</span>
          </div>
        </div>

        {/* Loading skeleton for steps */}
        <div className="bg-card/50 border border-border rounded-lg p-8">
          <div className="space-y-6">
            {[1, 2, 3].map((step) => (
              <Card
                key={step}
                className="transition-all duration-200 border-border">
                <div className="w-full p-6">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 mt-1">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-gc-med bg-muted animate-pulse"></div>
                    </div>
                    <div className="flex-1 text-left space-y-2">
                      <div className="h-5 bg-muted rounded animate-pulse w-48"></div>
                      <div className="h-4 bg-muted rounded animate-pulse w-72"></div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-12">
      {/* Header Section */}
      <div className="text-center space-y-6 pb-8 border-b border-border">
        <div className="space-y-3">
          <h1 className="text-4xl font-gc-demi-bold text-foreground leading-tight">
            Welcome to Maitai
            {userName && (
              <span className="text-muted-foreground">, {userName}</span>
            )}
          </h1>
          <p className="text-muted-foreground text-xl font-gc-reg">
            Get reliable LLM output in 3 steps
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-6">
          <div className="flex items-center gap-3">
            <div className="text-sm font-gc-med text-foreground">
              {completedSteps}/3 complete
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={cn(
                    "w-2 h-2 rounded-full transition-colors",
                    step <= completedSteps ? "bg-maitai-green" : "bg-muted"
                  )}
                />
              ))}
            </div>
          </div>
          {allStepsComplete && (
            <Button
              variant="outline"
              size="sm"
              className="border-maitai-green text-maitai-green hover:bg-maitai-green hover:text-white"
              asChild>
              <a
                href="/applications"
                className="inline-flex items-center gap-2">
                Continue
                <ArrowRightIcon className="w-4 h-4" />
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Setup Steps */}
      <div className="bg-card/50 border border-border rounded-lg p-8">
        <div className="space-y-6">
          {/* Step 1: Install */}
          <Step
            stepNumber={1}
            title="Install the Maitai SDK"
            description="Choose your language and install our SDK"
            icon={<TerminalIcon className="w-4 h-4" />}
            completed={dataReadyTutorial}>
            <div className="space-y-4">
              <CodeBlock items={installTabs} />
            </div>
          </Step>

          {/* Step 2: Implementation */}
          <Step
            stepNumber={2}
            title="Make your first request"
            description="Import Maitai and send a chat completion request"
            icon={<CodeIcon className="w-4 h-4" />}
            completed={dataReadyTutorial}>
            <div className="space-y-6">
              <CodeBlock items={codeExampleTabs} />

              <div className="flex justify-between items-center">
                <Button variant="ghost" size="sm" asChild>
                  <a
                    href="https://docs.trymaitai.ai/get_started/quickstart"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-muted-foreground">
                    View quickstart guide
                    <ExternalLinkIcon className="w-3 h-3" />
                  </a>
                </Button>
              </div>
            </div>
          </Step>

          {/* Step 3: Payment Method */}
          <Step
            stepNumber={3}
            title="Add payment method"
            description="Add a payment method to start using Maitai"
            icon={<CreditCardIcon className="w-4 h-4" />}
            completed={paymentMethods.length > 0}>
            <div className="space-y-4">
              {paymentMethods.length > 0 ? (
                <div className="flex items-center gap-3 p-4 bg-maitai-green/10 border border-maitai-green/20 rounded-lg">
                  <CheckIcon className="w-4 h-4 text-maitai-green" />
                  <span className="text-sm">
                    {paymentMethods.length} payment method
                    {paymentMethods.length !== 1 ? "s" : ""} added
                  </span>
                </div>
              ) : (
                <Button variant="outline" asChild>
                  <a href="/billing" className="inline-flex items-center gap-2">
                    <CreditCardIcon className="w-4 h-4" />
                    Add payment method
                  </a>
                </Button>
              )}
            </div>
          </Step>
        </div>
      </div>

      {/* Completion State */}
      {allStepsComplete && (
        <div className="bg-card border border-border rounded-lg p-8">
          <div className="text-center space-y-6">
            <div className="space-y-3">
              <h3 className="text-2xl font-gc-demi-bold text-foreground">
                Ready to build
              </h3>
              <p className="text-muted-foreground text-lg max-w-md mx-auto">
                Your setup is complete. Start building with Maitai.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
              <Button variant="default" asChild>
                <a
                  href="/applications"
                  className="inline-flex items-center gap-2">
                  View applications
                  <ArrowRightIcon className="w-4 h-4" />
                </a>
              </Button>
              <Button variant="ghost" asChild>
                <a
                  href="https://docs.trymaitai.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2">
                  Read documentation
                  <ExternalLinkIcon className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="border-t border-border pt-8">
        <div className="text-center space-y-6">
          <h3 className="text-xl font-gc-med text-foreground">Need help?</h3>
          <div className="flex flex-col sm:flex-row gap-6 items-center justify-center">
            <Button variant="ghost" size="sm" asChild>
              <a
                href="https://docs.trymaitai.ai/get_started/quickstart"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2">
                Quickstart guide
                <ExternalLinkIcon className="w-3 h-3" />
              </a>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a
                href="https://docs.trymaitai.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2">
                API reference
                <ExternalLinkIcon className="w-3 h-3" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tutorial;
